/**
 * Store Finder + Store Page runtime.
 *
 * Two Alpine factories -- `storeFinder` (multi-pin map + filterable list)
 * and `storePage` (single-pin map + "Set As My Store" wiring). Both
 * share a single Google Maps JavaScript API loader (`ensureMapsLoaded`)
 * so multiple maps on one page never re-bootstrap the API.
 *
 * Performance contract:
 *   - Map data is server-rendered into `<script
 *     type="application/json">` payloads. No client-side fetch for the
 *     pin list ever.
 *   - The Maps JS API script only loads when at least one map
 *     container enters the viewport (IntersectionObserver). Below the
 *     fold or in print, the script is never injected.
 *   - Geocode lookups share `sessionStorage["eckerleys:geocode_cache"]`
 *     with `find-in-store.js` -- typing the same postcode twice never
 *     hits Places API again within a browser session.
 *   - Filter / sort happens client-side on the in-memory list; no
 *     re-render fetch.
 *
 * Preferred-store wiring re-uses the FIS `Alpine.store('findInStore')`
 * contract -- `setPreferredStore({ locationId, name, latitude,
 * longitude })` dispatches `fis:store-set` on `document`, which the
 * FIS-aware filter-bar trigger and PDP CTA already subscribe to.
 */
(function () {
  const STORAGE_GEOCODE = 'eckerleys:geocode_cache';
  const STORAGE_PREFERRED_STORE = 'eckerleys:preferred_store';
  const PLACES_SEARCH_TEXT = 'https://places.googleapis.com/v1/places:searchText';
  const PLACES_FIELD_MASK = 'places.location,places.formattedAddress';

  // ----- Pure helpers ------------------------------------------------

  function readJSON(storage, key, fallback) {
    try {
      const raw = storage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (err) {
      return fallback;
    }
  }

  function writeJSON(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (err) {
      /* quota / disabled -- non-fatal */
    }
  }

  function normaliseQuery(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function locationGidToNumeric(gid) {
    const match = String(gid || '').match(/Location\/(\d+)/);
    return match ? match[1] : null;
  }

  function distanceKm(originLat, originLon, locLat, locLon) {
    if (originLat == null || originLon == null || locLat == null || locLon == null) {
      return null;
    }
    const R = 6371;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(locLat - originLat);
    const dLon = toRad(locLon - originLon);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(originLat)) * Math.cos(toRad(locLat)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(km) {
    if (km == null || Number.isNaN(km)) return '';
    if (km < 10) return km.toFixed(1) + ' km';
    return Math.round(km) + ' km';
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[ch]);
  }

  // ----- Maps API loader (singleton) ---------------------------------

  let mapsLoaderPromise = null;

  function ensureMapsLoaded(apiKey) {
    if (mapsLoaderPromise) return mapsLoaderPromise;
    if (!apiKey) {
      mapsLoaderPromise = Promise.reject(new Error('store_map_no_key'));
      return mapsLoaderPromise;
    }
    if (window.google && window.google.maps) {
      mapsLoaderPromise = Promise.resolve(window.google.maps);
      return mapsLoaderPromise;
    }
    mapsLoaderPromise = new Promise((resolve, reject) => {
      window.__storeMapInit = function () {
        resolve(window.google.maps);
      };
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      const url =
        'https://maps.googleapis.com/maps/api/js' +
        '?key=' + encodeURIComponent(apiKey) +
        '&v=weekly' +
        '&libraries=marker' +
        '&loading=async' +
        '&callback=__storeMapInit';
      script.src = url;
      script.onerror = () => reject(new Error('store_map_load_failed'));
      document.head.appendChild(script);
    });
    return mapsLoaderPromise;
  }

  // ----- Places API (geocode for search submit) ----------------------

  function geocode(query, apiKey) {
    if (!apiKey) return Promise.resolve(null);
    const normalised = normaliseQuery(query);
    if (!normalised) return Promise.resolve(null);
    const cache = readJSON(window.sessionStorage, STORAGE_GEOCODE, {});
    if (cache[normalised]) return Promise.resolve(cache[normalised]);
    return fetch(PLACES_SEARCH_TEXT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': PLACES_FIELD_MASK
      },
      body: JSON.stringify({
        textQuery: query,
        languageCode: 'en',
        regionCode: 'AU'
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error('places_unavailable');
        return res.json();
      })
      .then((payload) => {
        const place = payload && payload.places && payload.places[0];
        if (!place || !place.location) return null;
        const coords = {
          latitude: place.location.latitude,
          longitude: place.location.longitude,
          formattedAddress: place.formattedAddress || ''
        };
        cache[normalised] = coords;
        writeJSON(window.sessionStorage, STORAGE_GEOCODE, cache);
        return coords;
      })
      .catch(() => null);
  }

  // ----- Marker pin SVG ----------------------------------------------
  //
  //   Inline SVG data URL passed to `google.maps.Marker` via the `icon`
  //   option. The classic Marker class works on raster maps without a
  //   registered Map ID -- AdvancedMarkerElement would need one. The
  //   palette matches the map pin (crimson droplet, white inset).

  // Pin asset is served via Shopify's asset CDN (icon-map-marker.svg).
  // The url is read from the map root's data-store-map-pin-url attr at
  // marker creation; per-map closures cache the resolved URL so each
  // map instance only reads the attribute once.

  // ----- Lazy loader: only init a map when in view --------------------

  function whenInView(element, callback) {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const obs = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          callback();
        }
      });
    }, { rootMargin: '200px' });
    obs.observe(element);
  }

  // ----- Map renderers (finder + detail) ------------------------------

  function renderFinderMap(rootEl, stores, opts) {
    const apiKey = rootEl.dataset.storeMapKey;
    const zoom = parseInt(rootEl.dataset.storeMapZoom, 10) || 4;
    const pinUrl = rootEl.dataset.storeMapPinUrl;
    const canvas = rootEl.querySelector('.store-map__canvas');
    const onSelect = opts.onSelect || (() => {});
    const state = { map: null, markers: new Map(), info: null, lastSelected: null };

    function show() {
      ensureMapsLoaded(apiKey)
        .then((maps) => {
          const initial = pickAllView();
          state.map = new maps.Map(canvas, {
            center: initial.center,
            zoom: initial.zoom,
            disableDefaultUI: false,
            // Hide platform chrome not in the design:
            // Map/Satellite switcher, fullscreen toggle, Street View
            // pegman. Zoom + drag are retained.
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            clickableIcons: false,
            gestureHandling: 'cooperative'
          });
          state.info = new maps.InfoWindow({ disableAutoPan: false });

          paintMarkers(stores);
          rootEl.setAttribute('data-store-map-ready', '');
        })
        .catch(() => {
          // Map failed: keep the placeholder, the list still works.
          rootEl.setAttribute('data-store-map-error', '');
        });
    }

    function paintMarkers(list) {
      if (!state.map) return;
      // Recycle existing markers; only add missing handles and hide stale ones.
      const fresh = new Set();
      list.forEach((store) => {
        fresh.add(store.handle);
        if (state.markers.has(store.handle)) {
          state.markers.get(store.handle).setMap(state.map);
          return;
        }
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        const marker = new google.maps.Marker({
          map: state.map,
          position: { lat, lng },
          title: store.name,
          icon: {
            url: pinUrl,
            scaledSize: new google.maps.Size(39, 50),
            anchor: new google.maps.Point(19, 50)
          }
        });
        marker.addListener('click', () => {
          openInfoWindow(store, marker);
          onSelect(store.handle);
        });
        state.markers.set(store.handle, marker);
      });
      state.markers.forEach((marker, handle) => {
        if (!fresh.has(handle)) {
          marker.setMap(null);
        }
      });
    }

    function fitToMarkers() {
      if (!state.map) return;
      const visibleHandles = new Set(stores.map((s) => s.handle));
      const bounds = new google.maps.LatLngBounds();
      let count = 0;
      stores.forEach((store) => {
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        bounds.extend({ lat, lng });
        count += 1;
      });
      if (count === 0) return;
      if (count === 1) {
        state.map.setCenter(bounds.getCenter());
        state.map.setZoom(13);
      } else {
        state.map.fitBounds(bounds, 64);
      }
    }

    function openInfoWindow(store, marker) {
      if (!state.info || !state.map) return;
      const directionsUrl = store.directions_url ||
        ('https://www.google.com/maps/dir/?api=1&destination=' + store.latitude + ',' + store.longitude);
      const hours = openingHoursToday(store.opening_hours, store.state_code);
      const callHref = store.phone_url || (store.phone ? 'tel:' + store.phone.replace(/\s/g, '') : '');
      // Mirrors the FIS Tile: 273w tile with
      // 16/16/18/16 padding, gap 16 between store-info block and links
      // block. Store info: 20px Lora 500 title, 14px Roboto address
      // (muted), 14px Roboto hours (black). Links: 3 ghost-underline
      // small actions in a horizontal row with 12px gap.
      const html =
        '<div class="store-map__info">' +
        '<div class="store-map__info-store">' +
        '<div class="store-map__info-text">' +
        '<h3 class="store-map__info-title">' + escapeHtml(store.name) + '</h3>' +
        '<p class="store-map__info-address">' +
        escapeHtml([store.address_line_1, store.city, store.state_code, store.postcode].filter(Boolean).join(', ')) +
        '</p>' +
        (hours ? '<p class="store-map__info-hours">' + escapeHtml(hours) + '</p>' : '') +
        '</div>' +
        '<div class="store-map__info-actions">' +
        (store.url ? '<a class="store-map__info-link" href="' + escapeHtml(store.url) + '">View Store</a>' : '') +
        '<a class="store-map__info-link" href="' + escapeHtml(directionsUrl) + '" target="_blank" rel="noopener">Directions</a>' +
        (callHref ? '<a class="store-map__info-link" href="' + escapeHtml(callHref) + '">Call Store</a>' : '') +
        '</div>' +
        '</div>' +
        '</div>';
      state.info.setContent(html);
      state.info.open({ anchor: marker, map: state.map });
      state.lastSelected = store.handle;
    }

    whenInView(rootEl, show);

    return {
      paint(list, options) {
        stores = list;
        paintMarkers(list);
        // Only refit the map bounds when the caller explicitly asks
        // (i.e. after a postcode search). On chip / list re-sort we
        // leave the camera at the default AU-wide view.
        if (options && options.refit) fitToMarkers();
      },
      focus(handle, opts) {
        const marker = state.markers.get(handle);
        const store = stores.find((s) => s.handle === handle);
        if (!marker || !store || !state.map) return;
        openInfoWindow(store, marker);
        // Higher zoom than the state chip view -- enough to show the
        // store inside its suburb context.
        const targetZoom = (opts && opts.zoom) || 13;
        state.map.panTo(marker.position);
        if (state.map.getZoom() < targetZoom) {
          state.map.setZoom(targetZoom);
        }
      },
      showState(code) {
        if (!state.map) return;
        const view = code === 'all' || !STATE_VIEWS[code]
          ? pickAllView()
          : STATE_VIEWS[code];
        state.map.panTo(view.center);
        state.map.setZoom(view.zoom);
      },
      closeInfo() {
        if (state.info) state.info.close();
      }
    };
  }

  function renderDetailMap(rootEl, store) {
    const apiKey = rootEl.dataset.storeMapKey;
    const zoom = parseInt(rootEl.dataset.storeMapZoom, 10) || 14;
    const pinUrl = rootEl.dataset.storeMapPinUrl;
    const canvas = rootEl.querySelector('.store-map__canvas');

    function show() {
      ensureMapsLoaded(apiKey)
        .then((maps) => {
          const lat = parseFloat(store.latitude);
          const lng = parseFloat(store.longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
          const map = new maps.Map(canvas, {
            center: { lat, lng },
            zoom,
            disableDefaultUI: true,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            gestureHandling: 'cooperative'
          });
          new google.maps.Marker({
            map,
            position: { lat, lng },
            title: store.name,
            icon: {
              url: pinUrl,
              scaledSize: new google.maps.Size(39, 50),
              anchor: new google.maps.Point(19, 50)
            }
          });
          rootEl.setAttribute('data-store-map-ready', '');
        })
        .catch(() => {
          rootEl.setAttribute('data-store-map-error', '');
        });
    }

    whenInView(rootEl, show);
  }

  /* Today's trading hours for one store, on that STORE's clock -- a
     Perth store viewed from Sydney is not on the viewer's day, and from
     October Queensland sits an hour behind the daylight-saving states.
     `stateCode` picks the zone; see `window.theme.nowInZone`.

     Times are stored as 24-hour 'HH:MM' and formatted for display here;
     nothing customer-facing should render them raw. */
  function openingHoursToday(hours, stateCode) {
    if (!hours) return '';
    const { day } = window.theme.nowInZone(window.theme.zoneForState(stateCode));
    const slot = hours[day];
    if (!slot) return '';
    if (slot.closed) return 'Closed Today';
    if (slot.open && slot.close) {
      return 'Open Today: ' + window.theme.displayTime(slot.open) +
        ' - ' + window.theme.displayTime(slot.close);
    }
    return '';
  }

  // Default "all stores" view -- hand-tuned centre + zoom per viewport.
  // fitBounds(box) padded the perpendicular axis (Indonesia / Malaysia
  // leaked in on mobile 1:1 aspect), so we set centre+zoom directly.
  // Fractional zoom is supported on the vector renderer (Google Maps
  // JS API v3, default since 2023) -- 4.5 desktop sits between 4 (too
  // zoomed out, Indonesia / Malaysia visible) and 5 (crops AU coasts).
  const AU_VIEW_DESKTOP = { center: { lat: -27, lng: 134 }, zoom: 4.5 };
  const AU_VIEW_MOBILE = { center: { lat: -27, lng: 134 }, zoom: 3.5 };

  function pickAllView() {
    return window.matchMedia('(max-width: 1024px)').matches
      ? AU_VIEW_MOBILE
      : AU_VIEW_DESKTOP;
  }

  // State-level map views for the chip filter. Centre + zoom approximate
  // each Australian state so the moderate-zoom transition lands on the
  // right region without auto-fitting tightly to data (which would
  // bounce around as merchants add/remove store entries).
  const STATE_VIEWS = {
    ACT: { center: { lat: -35.45, lng: 149.05 }, zoom: 9 },
    NSW: { center: { lat: -32.5, lng: 147 }, zoom: 6 },
    QLD: { center: { lat: -22.5, lng: 144 }, zoom: 5 },
    SA:  { center: { lat: -30, lng: 135 }, zoom: 5 },
    TAS: { center: { lat: -42, lng: 146.5 }, zoom: 7 },
    VIC: { center: { lat: -37, lng: 144 }, zoom: 7 },
    WA:  { center: { lat: -26, lng: 121 }, zoom: 5 },
    NT:  { center: { lat: -20, lng: 133 }, zoom: 5 }
  };

  // ----- Preferred-store store (shared with FIS) ----------------------

  function ensureFindInStoreStore() {
    if (!window.Alpine) return;
    if (window.Alpine.store('findInStore')) return;
    window.Alpine.store('findInStore', {
      preferredStore: readJSON(window.localStorage, STORAGE_PREFERRED_STORE, null),
      currentVariantId: null,
      currentBadge: null,
      cardStockByVariant: {},
      placesUnavailable: false,
      hasExplicitVariant: false,
      config: {},
      i18n: {},
      setPreferredStore(payload) {
        this.preferredStore = payload;
        if (payload) {
          writeJSON(window.localStorage, STORAGE_PREFERRED_STORE, payload);
          const numeric = locationGidToNumeric(payload.locationId);
          if (numeric) {
            const form = new FormData();
            form.append('attributes[_preferred_store_id]', numeric);
            fetch('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
              body: form
            }).catch(() => {});
          }
        } else {
          try { window.localStorage.removeItem(STORAGE_PREFERRED_STORE); } catch (err) { /* noop */ }
        }
        document.dispatchEvent(new CustomEvent('fis:store-set', { detail: payload }));
      },
      setCurrentBadge(b) { this.currentBadge = b; },
      setCurrentVariantId(id) {
        if (this.currentVariantId === id) return;
        this.currentVariantId = id;
        if (!id) this.currentBadge = null;
      },
      setCardStockByVariant(map) { this.cardStockByVariant = map || {}; }
    });
  }

  // ----- Alpine: storeFinder factory ----------------------------------

  document.addEventListener('alpine:init', () => {
    ensureFindInStoreStore();

    // Shared store so the server-rendered <details> entries can read
    // distance labels via $store.storeFinder.distanceFor(handle) without
    // each entry having to access the parent factory.
    if (!window.Alpine.store('storeFinder')) {
      window.Alpine.store('storeFinder', {
        distances: {},
        distanceFor(handle) { return this.distances[handle] || ''; },
        setDistances(map) { this.distances = map || {}; }
      });
    }

    /* Shared Google Places autocomplete mixin — same implementation as the
     * PDP FIS block. Reads the API key back from the storeFinder factory
     * on demand, resolves the picked suggestion to coordinates, and hands
     * them off to `onAutocompleteResolved` to drive the map + list.
     * Outside-click boundary is scoped to the form so a click on the map
     * or the store list also closes the dropdown — matches FIS UX. */
    const autocompleteMixin = (window.EckerleysPlaces &&
      window.EckerleysPlaces.createAutocompleteMixin({
        idPrefix: 'store-finder-suggestion',
        containerSelector: '.store-finder__form',
        getApiKey: function () { return this.apiKey; },
        isUnavailable: function () { return this.placesUnavailable; },
        setUnavailable: function (flag) { this.placesUnavailable = flag; },
        onSelect: function (coords, item) { this.onAutocompleteResolved(coords, item); },
        onSelectError: function () {
          this.error = this.i18n.geocode_failed || 'Something went wrong looking up that address.';
        }
      })) || {};

    window.Alpine.data('storeFinder', () => ({
      ...autocompleteMixin,
      query: '',
      activeState: 'all',
      stores: [],
      visibleHandles: {},
      visibleCount: 0,
      selectedHandle: '',
      origin: null,
      error: '',
      mapHandle: null,
      i18n: {},
      apiKey: '',
      placesUnavailable: false,

      init() {
        // Cache the root element. `this.$root` isn't proxied through
        // Alpine's inner x-for iteration scopes, so any method invoked
        // via a suggestion click (@mousedown inside x-for) loses access
        // to `$root`. Store the DOM reference once at init and use it
        // for all subsequent DOM queries.
        const root = this.$root || this.$el;
        this.rootEl = root;
        this.activeState = root.dataset.initialState || 'all';
        const payloadScript = root.querySelector('script[data-store-finder-payload]');
        // Locale strings that pass through Shopify's `| t | json` come
        // back with HTML entities (`&#39;`) intact. Alpine's `x-text`
        // hands them to `textContent` which doesn't decode entities, so
        // messages like "We couldn't find that…" would render literally
        // as "We couldn&#39;t find that…". Decode via the browser's
        // native HTML parser once at hydration.
        const entityDecoder = document.createElement('textarea');
        const decodeEntities = (value) => {
          if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
          entityDecoder.innerHTML = value;
          return entityDecoder.value;
        };
        if (payloadScript) {
          try {
            const payload = JSON.parse(payloadScript.textContent || '{}');
            this.stores = (payload.stores || []).map((store) => Object.assign({}, store, {
              distance_km: null,
              distance_label: '',
              state_code: (store.state_code || '').toUpperCase()
            }));
            const rawI18n = payload.i18n || {};
            this.i18n = Object.keys(rawI18n).reduce((acc, key) => {
              acc[key] = decodeEntities(rawI18n[key]);
              return acc;
            }, {});
          } catch (err) {
            this.stores = [];
          }
        }

        // Pull the Google Maps API key from the map container (rendered
        // server-side via `settings.google_maps_api_key`). Shared key
        // across the finder + detail maps + the FIS drawer.
        const mapRoot = root.querySelector('[data-store-map]');
        this.apiKey = mapRoot ? mapRoot.dataset.storeMapKey || '' : '';

        // Bind the shared Places autocomplete mixin (session token +
        // outside-click boundary). Only mounts when the mixin is present
        // and an API key is available.
        if (this.apiKey && typeof this.initAutocomplete === 'function') {
          this.initAutocomplete();
        }

        this.bootMap(mapRoot);
        this.recomputeVisible();

        // Mobile-only: auto-expand the first store entry (the mobile
        // design shows Adelaide CBD pre-expanded on first render).
        // Desktop keeps all collapsed.
        if (window.matchMedia('(max-width: 1024px)').matches) {
          const first = root.querySelector('[data-first-entry]');
          if (first && !first.open) first.open = true;
        }
      },

      bootMap(mapRoot) {
        if (!mapRoot) return;
        this.mapHandle = renderFinderMap(mapRoot, this.stores, {
          onSelect: (handle) => { this.selectedHandle = handle; }
        });
      },

      recomputeVisible() {
        let list = this.stores.slice();
        if (this.activeState && this.activeState !== 'all') {
          list = list.filter((s) => (s.state_code || '').toUpperCase() === this.activeState);
        }
        const distances = {};
        if (this.origin) {
          list.forEach((store) => {
            const km = distanceKm(
              this.origin.latitude,
              this.origin.longitude,
              parseFloat(store.latitude),
              parseFloat(store.longitude)
            );
            store.distance_km = km;
            store.distance_label = formatDistance(km);
            distances[store.handle] = store.distance_label;
          });
          list.sort((a, b) => {
            if (a.distance_km == null) return 1;
            if (b.distance_km == null) return -1;
            return a.distance_km - b.distance_km;
          });
        } else {
          list.forEach((store) => {
            store.distance_km = null;
            store.distance_label = '';
          });
          list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }
        const visible = {};
        list.forEach((s) => { visible[s.handle] = true; });
        this.visibleHandles = visible;
        this.visibleCount = list.length;
        this.$store.storeFinder.setDistances(distances);
        this.reorderList(list);
        // Refit the map only when an origin is set (postcode search).
        // Chip filters keep the AU-wide camera.
        if (this.mapHandle) this.mapHandle.paint(list, { refit: !!this.origin });
      },

      reorderList(list) {
        // Reorder server-rendered <details> entries by current sort order.
        // The map of handle -> distance is already in `distances`; here we
        // physically move DOM nodes so the visual list matches the sort.
        const container = this.rootEl && this.rootEl.querySelector('.store-finder__list');
        if (!container) return;
        list.forEach((store) => {
          const el = container.querySelector('[data-handle="' + store.handle + '"]');
          if (el) container.appendChild(el);
        });
      },

      isVisible(handle) {
        return !!this.visibleHandles[handle];
      },

      setActiveState(code) {
        this.activeState = code;
        this.recomputeVisible();
        // Pan/zoom the map to the chosen state. "all" falls back to
        // AU-wide.
        if (this.mapHandle) this.mapHandle.showState(code);
      },

      stateCount(code) {
        let n = 0;
        for (let i = 0; i < this.stores.length; i += 1) {
          if ((this.stores[i].state_code || '').toUpperCase() === code) n += 1;
        }
        return n;
      },

      /* Suggestions dropdown — driven by the shared Places autocomplete
       * mixin. This handler just proxies input into the mixin's debounced
       * fetcher (bound via x-model="query" + @input on the input). */
      onAutocomplete() {
        this.autocompleteQuery = this.query;
        this.onAutocompleteInput();
      },

      /* Called by the mixin when a suggestion has resolved to coordinates.
       * Mirrors the postcode submit path (`onSubmit`) so the map re-centres
       * and the list re-sorts by distance from the picked location. */
      onAutocompleteResolved(coords, item) {
        if (!coords) {
          this.error = this.i18n.geocode_failed || 'No match found for that suggestion.';
          this.origin = null;
          this.recomputeVisible();
          return;
        }
        this.error = '';
        this.query = (item && item.description) || this.autocompleteQuery || this.query;
        this.origin = Object.assign({}, coords, { source: 'places' });
        this.recomputeVisible();
        this.publishStoreSearch(this.searchTypeForQuery());
      },

      onSubmit() {
        this.error = '';
        const value = (this.query || '').trim();
        if (!value) return;
        geocode(value, this.apiKey).then((coords) => {
          if (!coords) {
            this.error = this.i18n.geocode_failed || 'No match found for that postcode or suburb.';
            this.origin = null;
            this.recomputeVisible();
            return;
          }
          this.origin = coords;
          this.recomputeVisible();
          this.publishStoreSearch(this.searchTypeForQuery());
        });
      },

      onClear() {
        this.query = '';
        this.autocompleteQuery = '';
        if (typeof this.closeAutocomplete === 'function') this.closeAutocomplete();
        this.origin = null;
        this.error = '';
        this.recomputeVisible();
      },

      /* store_finder_search — one event per executed search;
         results_count is the visible store count (zero included). */
      publishStoreSearch(searchType) {
        if (!window.theme || typeof window.theme.publish !== 'function') return;
        window.theme.publish('eckersleys:store_finder_search', {
          search_type: searchType,
          results_count: this.visibleCount,
        });
      },
      /* Combined postcode/suburb field: a bare 3–4 digit query is a
         postcode, everything else a suburb. */
      searchTypeForQuery() {
        return /^\d{3,4}$/.test((this.query || '').trim()) ? 'postcode' : 'suburb';
      },

      onGeolocate() {
        if (!navigator.geolocation) {
          this.error = this.i18n.geocode_failed || 'Geolocation is not available.';
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.origin = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              source: 'geolocation'
            };
            this.error = '';
            this.recomputeVisible();
            this.publishStoreSearch('geolocation');
          },
          () => {
            this.error = this.i18n.geocode_failed || "Couldn't get your location.";
          },
          { timeout: 7000, maximumAge: 60000, enableHighAccuracy: false }
        );
      },

      onEntryToggle(handle, isOpen) {
        if (isOpen) {
          this.selectedHandle = handle;
          // Accordion opens trigger a high-zoom focus on the store --
          // tighter than the state chip view so the customer can see
          // the surrounding streets.
          if (this.mapHandle) this.mapHandle.focus(handle, { zoom: 14 });
        } else if (this.selectedHandle === handle) {
          this.selectedHandle = '';
          if (this.mapHandle) this.mapHandle.closeInfo();
        }
      },

      // Kept for parity with previous render path; unused now the list is
      // server-rendered, but other call sites might re-introduce x-html.
      renderCard(store) {
        // Plain template literal -- Alpine `x-html` consumes the string.
        const address = [store.address_line_1, store.address_line_2]
          .filter(Boolean).join(', ');
        const cityLine = [store.city, store.state_code, store.postcode]
          .filter(Boolean).join(' ');
        const hoursRows = renderHoursRows(store.opening_hours);
        const callHref = store.phone_url || (store.phone ? 'tel:' + store.phone.replace(/\s/g, '') : '');
        const directionsUrl = store.directions_url ||
          ('https://www.google.com/maps/dir/?api=1&destination=' + store.latitude + ',' + store.longitude);
        return (
          '<div class="store-card">' +
          '<address class="store-card__address">' +
          (address ? '<p class="store-card__line">' + escapeHtml(address) + '</p>' : '') +
          (cityLine ? '<p class="store-card__line">' + escapeHtml(cityLine) + '</p>' : '') +
          '</address>' +
          (hoursRows ? '<dl class="store-card__hours">' + hoursRows + '</dl>' : '') +
          '<div class="store-card__actions">' +
          (store.url ? '<a class="button button--text-link button--black button--sm" href="' + escapeHtml(store.url) + '"><span class="button__label">View Store</span></a>' : '') +
          '<a class="button button--text-link button--black button--sm" href="' + escapeHtml(directionsUrl) + '" target="_blank" rel="noopener"><span class="button__label">Directions</span></a>' +
          (callHref ? '<a class="button button--text-link button--black button--sm" href="' + escapeHtml(callHref) + '"><span class="button__label">Call Store</span></a>' : '') +
          '</div>' +
          '</div>'
        );
      }
    }));

    // ----- Alpine: storePage factory --------------------------------

    window.Alpine.data('storePage', () => ({
      locationId: '',
      storeName: '',
      latitude: null,
      longitude: null,
      detailMapBooted: false,

      init() {
        const root = this.$root || this.$el;
        this.locationId = root.dataset.locationId || '';
        this.storeName = root.dataset.storeName || '';
        const lat = parseFloat(root.dataset.latitude);
        const lng = parseFloat(root.dataset.longitude);
        this.latitude = Number.isNaN(lat) ? null : lat;
        this.longitude = Number.isNaN(lng) ? null : lng;
        const mapRoot = root.querySelector('[data-store-map]');
        const payload = root.querySelector('script[data-store-page-payload]');
        let store = {};
        if (payload) {
          try { store = JSON.parse(payload.textContent || '{}'); } catch (err) { store = {}; }
        }
        if (mapRoot && !this.detailMapBooted) {
          renderDetailMap(mapRoot, store);
          this.detailMapBooted = true;
        }
      },

      get isPreferred() {
        const preferred = this.$store.findInStore && this.$store.findInStore.preferredStore;
        if (!preferred || !preferred.locationId) return false;
        return preferred.locationId === this.locationId;
      },

      togglePreferred() {
        if (this.isPreferred) {
          this.$store.findInStore.setPreferredStore(null);
          return;
        }
        this.$store.findInStore.setPreferredStore({
          locationId: this.locationId,
          name: this.storeName,
          latitude: this.latitude,
          longitude: this.longitude
        });
      }
    }));

    // ----- Alpine: storeGalleryScroller factory ----------------------
    // Horizontal-scroll behaviour for the store detail gallery track.
    // Mirrors the shared horizontal-scroller snippet but keeps the
    // gallery's bespoke styling (aspect-ratio tiles, page-margin
    // padding). Prev / next buttons are hidden until the track
    // reports overflow via ResizeObserver.
    window.Alpine.data('storeGalleryScroller', () => ({
      atStart: true,
      atEnd: false,
      hasOverflow: false,
      _rafId: null,
      _resizeObserver: null,

      init() {
        this.measure();
        const track = this.$refs.track;
        if (window.ResizeObserver && track) {
          this._resizeObserver = new ResizeObserver(() => this.schedule());
          this._resizeObserver.observe(track);
        }
      },

      destroy() {
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
      },

      measure() {
        const track = this.$refs.track;
        if (!track) return;
        this.hasOverflow = track.scrollWidth > track.clientWidth + 1;
        this.atStart = track.scrollLeft <= 1;
        this.atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      },

      schedule() {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this.measure();
        });
      },

      step(direction) {
        const track = this.$refs.track;
        if (!track) return;
        track.scrollBy({ left: direction * track.clientWidth * 0.85, behavior: 'smooth' });
      }
    }));
  });

  function renderHoursRows(hours) {
    if (!hours) return '';
    const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    let rows = '';
    days.forEach((day) => {
      const slot = hours[day] || {};
      const label = day.charAt(0).toUpperCase() + day.slice(1);
      let value = '';
      let timeClass = '';
      if (slot.closed) {
        value = 'Closed';
        timeClass = ' store-card__hours-time--closed';
      } else if (slot.open && slot.close) {
        value = window.theme.displayTime(slot.open) + ' - ' + window.theme.displayTime(slot.close);
      } else {
        return;
      }
      rows +=
        '<div class="store-card__hours-row">' +
        '<dt class="store-card__hours-day">' + escapeHtml(label) + '</dt>' +
        '<dd class="store-card__hours-time' + timeClass + '">' + escapeHtml(value) + '</dd>' +
        '</div>';
    });
    return rows;
  }
})();

/* Map pin sizing -- kept here (not in a {% stylesheet %} tag) so the
 * pin matches across both maps without per-section duplication. */
(function injectPinStyles() {
  if (document.getElementById('store-pin-style')) return;
  const style = document.createElement('style');
  style.id = 'store-pin-style';
  style.textContent = [
    '.store-pin { width: 32px; height: 40px; color: rgb(var(--color-crimson, 227 0 80)); transform-origin: 50% 100%; transition: transform 200ms cubic-bezier(0.4,0,0.2,1); }',
    '.store-pin > svg { width: 100%; height: 100%; fill: currentColor; }',
    '.store-pin:hover, .store-pin.is-selected { transform: scale(1.12); }',
    '@media (max-width: 1024px) { .store-pin { width: 28px; height: 36px; } }'
  ].join('\n');
  document.head.appendChild(style);
})();
